import { useEffect, useState } from 'react'
import type { PurchaseCompletePayload } from '@ashport/shared'
import { A } from './art'
import { api, ApiError } from './net/api'
import { Elevate } from './shell/Elevate'
import { LeftNav } from './shell/LeftNav'
import { TopBar } from './shell/TopBar'
import { Ticker } from './shell/Ticker'
import type { Route } from './shell/routes'
import { Icon } from './ui/Icon'
import { Home } from './home/Home'
import { Jobs } from './screens/Jobs'
import { Crimes } from './screens/Crimes'
import { Ward } from './screens/Ward'
import { Families } from './screens/Families'
import { Market } from './screens/Market'
import { Territory } from './screens/Territory'
import { WarRoom } from './screens/WarRoom'
import { Contracts } from './screens/Contracts'
import { Fights } from './screens/Fights'
import { Hitlist } from './screens/Hitlist'
import { Scanner } from './screens/Scanner'
import { Ledger } from './screens/Ledger'
import { Gate } from './screens/Gate'
import { JestGate } from './screens/JestGate'
import { Letter, type LetterKind } from './screens/Letter'
import { Reveal } from './screens/Reveal'
import { Lineup } from './shell/Lineup'
import { BottomTabs } from './shell/BottomTabs'
import { Notifications, UpdateBar } from './shell/Notifications'
import { applyUpdate, onUpdateReady } from './pwa'
import { JEST } from './build'
import {
  jestCompletePurchase,
  jestEntryPayload,
  jestFirstMilestone,
  jestIncompletePurchases,
  jestLoaded,
  jestPlayerNow,
  jestReminders,
  jestRemindPref,
} from './jest'
import { useViewport } from './ui/viewport'
import { heatBand, useGame } from './game/state'
import { useSession } from './game/session'

/**
 * THE ONLY TWO PATHS THIS APPLICATION READS, and it reads them once.
 *
 * `assets.not_found_handling` is `single-page-application`, so `/verify` and `/reset` already
 * serve `index.html` and boot the app. What was missing was any read of the path at all. See
 * `screens/Letter.tsx` for why this is a literal check rather than a router. DESIGN-DELTAS 1t.2.
 *
 * Computed OUTSIDE the component, at module load, because `Letter` replaces the path as soon as
 * the player is finished with it and a value derived during render would then evaluate to null on
 * the next render and unmount the screen mid-sentence.
 */
function letterFromLocation(): { kind: LetterKind; token: string } | null {
  const path = window.location.pathname.replace(/\/+$/, '')
  const kind: LetterKind | null =
    path === '/verify' ? 'verify' : path === '/reset' ? 'reset' : null
  if (kind === null) return null
  return { kind, token: new URLSearchParams(window.location.search).get('t') ?? '' }
}

const LETTER = letterFromLocation()

/**
 * Every screen takes `go`, and only Home uses it.
 *
 * The panel that answers "what do I do now" has to be able to SEND the player somewhere, because
 * DESIGN-DELTAS 1t.4 rules that no route is gated and the first screen carries the ordering
 * instead. An ordering that names a screen and cannot open it is a list of instructions.
 */
const SCREENS: Record<Route, (p: { go: (r: Route) => void }) => React.ReactElement> = {
  home: Home,
  jobs: Jobs,
  crimes: Crimes,
  ward: Ward,
  families: Families,
  market: Market,
  territory: Territory,
  warroom: WarRoom,
  contracts: Contracts,
  hitlist: Hitlist,
  fights: Fights,
  scanner: Scanner,
  ledger: Ledger,
}

export default function App() {
  const [route, setRoute] = useState<Route>('home')
  const [purchase, setPurchase] = useState<PurchaseCompletePayload | null>(null)
  const [letter, setLetter] = useState(LETTER)
  const { tick, now, player } = useGame()
  const { phase, start, fault, lineup, clearLineup } = useSession()
  const loadLedger = useSession((s) => s.loadLedger)
  const notifyOpen = useSession((s) => s.notifyOpen)
  const closeNotify = useSession((s) => s.closeNotify)
  // PHASE 23. A new build installed beside this one and is waiting. The bar asks; nothing swaps
  // under a player until they say so, and "later" keeps it waiting until the next open.
  const [update, setUpdate] = useState<ServiceWorkerRegistration | null>(null)
  const [updateLater, setUpdateLater] = useState(false)
  useEffect(() => onUpdateReady((reg) => setUpdate(reg)), [])
  /*
   * THE FRAME HAS THREE SHAPES, since Phase 18. Desktop is the rail and the plate as they were.
   * Tablet is the same frame with the rail at 76px. Phone has no rail: a tab bar along the bottom
   * edge and a one-row top bar, because a 232px rail on a 393px screen left the game 161px, and
   * `docs/GAME-DESIGN.md` 1.2 puts the primary session on a phone.
   */
  const { vp } = useViewport()

  // One boot call. A 401 comes back as the signed-out phase rather than an error, because being
  // signed out is a state and not a failure.
  //
  // PHASE 26. Not under the Jest build: there is nobody to be signed in as until the door has
  // asked the platform, so the probe would be a guaranteed 401 and a round trip against the
  // platform's fifteen-second clock. `JestGate` signs in and then makes this same call (1ag.13).
  useEffect(() => {
    if (!JEST) void start()
  }, [start])

  // one clock for the whole app; panels read `now` from the store
  useEffect(() => {
    const t = setInterval(tick, 1000)
    return () => clearInterval(t)
  }, [tick])

  // Heat drives the global visual state, per the art direction
  useEffect(() => {
    document.documentElement.dataset.heat = heatBand(player.heat)
  }, [player.heat])

  useEffect(() => {
    document.documentElement.style.setProperty('--app-bg', `url(${A('brand.skyline')})`)
  }, [])

  /*
   * THE RETURN FROM STRIPE, and it is a QUERY PARAMETER because this application has no router.
   *
   * There is no react-router here and nothing anywhere reads `window.location.pathname`: `route`
   * is `useState<Route>('home')` and navigation is a `setRoute` call. So a success URL of
   * `/purchase/complete` would serve `index.html` through the SPA fallback, boot the app, land on
   * `home`, and silently ignore the path. `?purchase={CHECKOUT_SESSION_ID}` needs no router at
   * all, which is why the Checkout Session is created with it.
   *
   * THE WEBHOOK HAS ALMOST CERTAINLY ALREADY SETTLED THIS. Stripe waits up to ten seconds for a
   * 2xx from the webhook before redirecting the customer here. This calls the same fulfilment
   * function anyway, because "almost certainly" is not a guarantee and the function is built to
   * be safe to call twice.
   *
   * The parameter is stripped with `replaceState` either way, so a reload is not a second
   * fulfilment attempt and a shared URL does not carry somebody's session id.
   */
  useEffect(() => {
    if (phase !== 'ready' || JEST) return
    const params = new URLSearchParams(window.location.search)
    const id = params.get('purchase')
    if (!id) return
    window.history.replaceState({}, '', window.location.pathname)
    if (id === 'cancelled') {
      setPurchase({ state: 'cancelled', sku: null, granted: [], message: 'Nothing was charged.' })
      return
    }
    void api
      .purchaseComplete(id)
      .then((r) => {
        setPurchase(r)
        // The Ledger holds ownership, the wardrobe and the cap, and all three just moved.
        void loadLedger().catch(() => undefined)
      })
      .catch((e: unknown) => {
        setPurchase({
          state: 'pending',
          sku: null,
          granted: [],
          message:
            e instanceof ApiError
              ? e.message
              : 'We could not read that order back. If you were charged, it will still arrive.',
        })
      })
  }, [phase, loadLedger])

  /*
   * THE JEST BUILD'S FIRST READY FRAME. Phase 26, DESIGN-DELTAS 1ag.4, 1ag.7 and 1ag.10.
   *
   * Four things, once, when the boot read has answered under the Jest build, and nothing at all
   * under any other build, because `JEST` is a compile-time constant and this whole block is dead
   * code at ashport.city.
   *
   *   1. The overlay comes down. The platform exits a game that reports no progress in fifteen
   *      seconds; `JestGate` reported 30 before `init`, and this is the 100.
   *   2. INCOMPLETE PURCHASES ARE DRAINED. Their recommended flow: on every start, read the list
   *      the platform still holds, verify each on the server, complete the ones that landed. A
   *      purchase whose grant was lost to a closed tab arrives here, and a second delivery of one
   *      that already landed settles as `already`, so posting the list is always safe. The last
   *      line is shown as the purchase notice; the Ledger is re-read because ownership moved.
   *   3. Reminders are applied from the switch, which is on until the player turns it off.
   *   4. The entry payload's `route` is read once and honoured, which is how a text or a referral
   *      link lands the player on Jobs rather than on Home.
   */
  useEffect(() => {
    if (!JEST || phase !== 'ready') return
    jestLoaded()
    const drain = async () => {
      let last: { result: string; message: string; sku: string | null } | null = null
      let touched = false
      // Bounded, because `hasMore` is theirs and a platform that never says false would spin.
      for (let page = 0; page < 8; page++) {
        const batch = await jestIncompletePurchases()
        if (batch === null || batch.purchases.length === 0) break
        touched = true
        const out = await api.jestPurchase({ purchasesSigned: batch.purchasesSigned })
        for (const line of out.results) {
          if (line.result === 'settled' || line.result === 'already') {
            await jestCompletePurchase(line.purchaseToken).catch(() => null)
          }
          last = line
        }
        if (!batch.hasMore) break
      }
      if (touched) void loadLedger().catch(() => undefined)
      if (last !== null) {
        setPurchase({
          state: last.result === 'refused_cap' ? 'refused_cap' : last.result === 'refused' ? 'failed' : 'paid',
          sku: last.sku,
          granted: [],
          message: last.message,
        })
      }
    }
    drain().catch((e: unknown) => {
      setPurchase({
        state: 'pending',
        sku: null,
        granted: [],
        message:
          e instanceof ApiError
            ? e.message
            : 'We could not read that order back. If you were charged, it will still arrive.',
      })
    })
    jestReminders(jestRemindPref(), jestPlayerNow()?.registered === true)
    const wanted = jestEntryPayload().route
    if (typeof wanted === 'string' && wanted in SCREENS) setRoute(wanted as Route)
  }, [phase, loadLedger])

  /*
   * THE FIRST MILESTONE, ONCE. A tutorial-only name on their side, feature-detected on ours: the
   * platform wants the first meaningful moment, and level 2 is the first thing the game gives a
   * player that it did not give them at the door. Keyed in `localStorage` so it fires once per
   * browser rather than once per open.
   */
  useEffect(() => {
    if (!JEST || phase !== 'ready' || player.level < 2) return
    jestFirstMilestone()
  }, [phase, player.level])

  /*
   * THE EMAILED LINK WINS OVER EVERY OTHER STATE, INCLUDING A DEAD LINE.
   *
   * Above `down` deliberately: `/api/verify` and `/api/auth/reset/confirm` do not touch the Beat,
   * the Era or any Durable Object the boot read needs, so a player whose link arrives during
   * maintenance can still use it. Above `anon` too, because somebody following a reset link is by
   * definition not signed in and the Gate is not what they came for.
   */
  if (letter !== null) {
    return (
      <Letter
        kind={letter.kind}
        token={letter.token}
        onDone={() => {
          // The path goes before the screen does, so a reload is not a second attempt on a token
          // that has already been spent and a copied URL does not carry a live one.
          window.history.replaceState({}, '', '/')
          setLetter(null)
          void start()
        }}
      />
    )
  }

  // PHASE 26. Under the Jest build the platform holds the key and there is no form (1ag.1). The
  // door is on screen from the first frame, since it is what boots; a refusal keeps it there.
  if (phase === 'booting') return JEST ? <JestGate /> : <Booting />
  if (phase === 'anon') return JEST ? <JestGate /> : <Gate />
  if (phase === 'closed') return <EraClosed message={fault} />
  if (phase === 'down') return <Down message={fault} />

  return (
    <div style={{ display: 'flex', height: '100%', overflow: 'hidden' }}>
      {/*
       * `badges={{ contracts: 3 }}` WAS HERE, and it was the first thing a new player was told
       * that was not true.
       *
       * A red 3 on Contracts, for every account, from second one, for a board that had nothing on
       * it. It is deleted rather than corrected because there is no notification count anywhere in
       * this game to correct it to: no table counts unread anything, and inventing one to feed a
       * badge would be building the system backwards from its decoration. `LeftNav` keeps the
       * `badges` prop, because the day something does count, the rail can render it.
       */}
      {/* The password prompt, in the shell because either guarded verb can raise it. */}
      <Elevate />
      {vp !== 'phone' && <LeftNav route={route} go={setRoute} compact={vp === 'tablet'} />}
      <div style={{ display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 }}>
        <TopBar />
        <Unverified />
        <main style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          {(() => {
            const Screen = SCREENS[route]
            return <Screen go={setRoute} />
          })()}
        </main>
        <Ticker now={now} go={setRoute} compact={vp === 'phone'} />
        {vp === 'phone' && <BottomTabs route={route} go={setRoute} />}
      </div>
      {/*
       * THE LINEUP, over everything. GAME-DESIGN 13.2.
       *
       * Rendered here rather than inside a screen because it is not somewhere a player goes: the
       * server refuses every tokened verb while one is open, so an interstitial that lived on one
       * screen would be a dead end from any other. It is the only automatic consequence in the
       * anti-automation ladder and it is three seconds long.
       */}
      {lineup && <Lineup data={lineup} onDone={clearLineup} />}
      {purchase && <PurchaseNotice r={purchase} onDone={() => setPurchase(null)} />}
      {notifyOpen && <Notifications onClose={closeNotify} />}
      {update && !updateLater && (
        <UpdateBar onReload={() => applyUpdate(update)} onLater={() => setUpdateLater(true)} />
      )}
    </div>
  )
}

/**
 * ONE LINE, ABOVE THE GAME, WHEN THE ADDRESS ON THE ACCOUNT IS NOT CONFIRMED.
 *
 * Every account this game has ever created is `unverified`, because the link was mailed and no
 * client path could consume it, and nothing anywhere said so. This is the smallest honest fix: the
 * player is told what is unfinished and given the one button that finishes it.
 *
 * **PLAY IS NOT GATED ON IT, AND WILL NOT BE.** GAME-DESIGN 11.1 puts email capture at minute
 * eight and CCP's stated reason for killing their linear tutorial is that a player gets stuck on a
 * step they cannot figure out. A verification wall in front of the first ten minutes is that step,
 * before the game has given anybody a reason to want the account. What verification actually buys
 * is recovery, and recovery only matters once there is something to recover.
 *
 * It is a STRIP rather than a modal for the same reason. A modal is a step. A line is a fact.
 */
function Unverified() {
  const status = useSession((s) => s.boot?.accountStatus)
  const resend = useSession((s) => s.resendVerify)
  const [said, setSaid] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [hidden, setHidden] = useState(false)
  if (status !== 'unverified' || hidden) return null
  return (
    <div
      style={{
        flex: 'none',
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        padding: '4px 12px',
        borderBottom: '1px solid var(--line-brass)',
        background: 'color-mix(in srgb, var(--gold) 9%, transparent)',
        fontSize: 11.5,
        color: 'var(--ink-mid)',
      }}
    >
      <span style={{ minWidth: 0, flex: '1 1 0', lineHeight: 1.4 }}>
        {said ??
          'The address on this account is not confirmed. Until it is, a lost password cannot be recovered.'}
      </span>
      {said === null && (
        <button
          type="button"
          disabled={busy}
          onClick={() => {
            setBusy(true)
            void resend()
              .then(setSaid)
              .catch(() => setSaid('That did not send. Try again in a minute.'))
              .finally(() => setBusy(false))
          }}
          style={{
            background: 'none',
            border: 0,
            padding: 0,
            font: 'inherit',
            color: 'var(--gold)',
            textDecoration: 'underline',
            cursor: busy ? 'default' : 'pointer',
            flex: 'none',
            minHeight: 44,
          }}
        >
          {busy ? 'sending' : 'send it again'}
        </button>
      )}
      <button
        type="button"
        className="icon-btn"
        onClick={() => setHidden(true)}
        aria-label="Hide this line"
        style={{ background: 'none', border: 0, cursor: 'pointer', padding: 0, lineHeight: 0, flex: 'none' }}
      >
        <Icon n="close" s={12} c="var(--ink-dim)" />
      </button>
    </div>
  )
}

/**
 * What happened to the money, said once, over everything.
 *
 * OVER EVERYTHING RATHER THAN ON THE LEDGER, for the same reason the Lineup is: the player
 * arrives here from an external redirect and lands on whatever screen the app boots to, so a
 * notice that lived on one screen would be a notice most buyers never see.
 *
 * `refused_cap` gets the longest copy on the page, deliberately. It is the one outcome where the
 * money left the player's account and came back, and a player who is not told that in plain words
 * is a player filing a chargeback.
 */
function PurchaseNotice({ r, onDone }: { r: PurchaseCompletePayload; onDone: () => void }) {
  const title =
    r.state === 'paid'
      ? 'DONE'
      : r.state === 'refused_cap'
        ? 'REFUSED, AND REFUNDED'
        : r.state === 'pending'
          ? 'STILL CLEARING'
          : r.state === 'cancelled'
            ? 'CANCELLED'
            : 'NOT COMPLETED'
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        display: 'grid',
        placeItems: 'center',
        background: 'rgba(0,0,0,.72)',
        zIndex: 90,
        padding: 24,
      }}
    >
      <div
        style={{
          maxWidth: 460,
          background: 'var(--coal)',
          border: '1px solid var(--line-brass)',
          padding: '22px 24px',
          textAlign: 'center',
        }}
      >
        <div className="t-display" style={{ fontSize: 26, color: 'var(--gold-hot)', letterSpacing: '.05em' }}>
          {title}
        </div>
        <div style={{ color: 'var(--ink-mid)', marginTop: 12, fontSize: 13, lineHeight: 1.6 }}>
          {r.message}
        </div>
        {/* The receipt line is Stripe's story. Under Jest the platform took the money and holds the receipt (1ag.10). */}
        {r.state === 'paid' && !JEST && (
          <div style={{ color: 'var(--ink-dim)', marginTop: 8, fontSize: 12, lineHeight: 1.6 }}>
            A receipt is on its way to the address on the account. It will read as ASHPORT.CITY on
            your statement.
          </div>
        )}
        <button
          type="button"
          onClick={onDone}
          style={{
            marginTop: 18,
            padding: '8px 22px',
            background: 'transparent',
            border: '1px solid var(--line-brass)',
            color: 'var(--ink-hi)',
            cursor: 'pointer',
            font: 'inherit',
          }}
        >
          Back to the city
        </button>
      </div>
    </div>
  )
}

/** No spinner. A held frame of key art, because a first impression is worth one round trip. */
function Booting() {
  return (
    <div
      style={{
        height: '100%',
        display: 'grid',
        placeItems: 'center',
        background: `var(--coal) url(${A('brand.loading_key_art_wide')}) center / cover no-repeat`,
      }}
    >
      <div
        className="t-display"
        style={{ fontSize: 40, letterSpacing: '.1em', color: 'var(--ink)', textShadow: '0 2px 16px #000' }}
      >
        ASHPORT
      </div>
    </div>
  )
}

/**
 * THE ERA IS OVER, and this is not an error page.
 *
 * `ERA_CLOSED` fell through to `<Down />`, the same screen a dead network line draws, so a player
 * arriving at the end of a twelve-week season got "THE CITY IS CLOSED / Back shortly." That is the
 * single moment the whole reset design exists for, and GAME-DESIGN 10.2 is explicit that the gap
 * between Eras "runs as content" and is "never a blank come-back-later page. That is the moment a
 * Discord empties."
 *
 * What it can honestly offer today is the Reveal, and the Reveal is enough: `/api/ledger/reveal`
 * reads the `era` row and the player's own roll history and touches the Beat nowhere, so it keeps
 * answering after `currentBeat` has started refusing. The rest of 10.2's retrospective (the Legend
 * resolution, the Hall of Bosses update, the Era Monument) needs systems that do not exist yet;
 * `docs/DESIGN-DELTAS.md` 1q.8 names them rather than mocking them up here.
 */
function EraClosed({ message }: { message: string | null }) {
  return (
    <div
      style={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        background: `var(--coal) url(${A('brand.skyline')}) center / cover no-repeat`,
        padding: 16,
        gap: 12,
        overflow: 'hidden',
      }}
    >
      <div style={{ textAlign: 'center', flex: 'none', textShadow: '0 2px 16px #000' }}>
        <div className="t-display" style={{ fontSize: 34, color: 'var(--gold-hot)', letterSpacing: '.06em' }}>
          THE BOOKS ARE CLOSED
        </div>
        <div style={{ color: 'var(--ink-mid)', marginTop: 8, fontSize: 13 }}>
          {message ?? 'That Era is finished.'}
        </div>
        <div style={{ color: 'var(--ink-dim)', marginTop: 4, fontSize: 12 }}>
          Nothing you earned in it is gone from the record. Check the rolls.
        </div>
      </div>
      <div style={{ flex: 1, minHeight: 0, maxWidth: 1000, width: '100%', margin: '0 auto', display: 'flex' }}>
        <Reveal />
      </div>
    </div>
  )
}

function Down({ message }: { message: string | null }) {
  return (
    <div
      style={{
        height: '100%',
        display: 'grid',
        placeItems: 'center',
        background: 'var(--coal)',
        padding: 40,
        textAlign: 'center',
      }}
    >
      <div style={{ maxWidth: 460 }}>
        <div className="t-display" style={{ fontSize: 30, color: 'var(--ink)' }}>
          THE CITY IS CLOSED
        </div>
        <div style={{ color: 'var(--ink-mid)', marginTop: 12, fontSize: 13 }}>
          {message ?? 'Back shortly.'}
        </div>
      </div>
    </div>
  )
}
